import React from 'react';
import * as d3 from 'd3';
import { Box, Grid, Typography } from '@mui/material';
import { DynamicBarChart } from 'src/App/Components/Charts';
import {
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_800,
  COLOR_SECONDARY_2_600,
  COLOR_SECONDARY_2_800,
} from 'src/colors';
import {
  abbreviateNumber,
  convertCurrency,
  formatCurrency,
  getMlaMonthName,
  getPercentage,
} from 'src/utils/functions';
import { BarGraphData } from 'src/interfaces/idashboard';
import { exchangeRates, PERCENTILES, shortMonthNames } from 'src/const';
import { useCannotAccessMainKPI } from 'src/hooks/useFeatureAccess';

const dummyGraphData = [
  {
    key: 'Jan',
    keyShort: 'Jan',
    keyLong: 'Jan',
    value: 1594,
  },
  {
    key: 'Feb',
    keyShort: 'Feb',
    keyLong: 'Feb',
    value: 3046,
  },
  {
    key: 'Mar',
    keyShort: 'Mar',
    keyLong: 'Mar',
    value: 1358,
  },
  {
    key: 'Apr',
    keyShort: 'Apr',
    keyLong: 'Apr',
    value: -34,
  },
  {
    key: 'May',
    keyShort: 'May',
    keyLong: 'May',
    value: -548,
  },
  {
    key: 'Jun',
    keyShort: 'Jun',
    keyLong: 'Jun',
    value: -2866,
  },
  {
    key: 'Jul',
    keyShort: 'Jul',
    keyLong: 'Jul',
    value: -3331,
  },
  {
    key: 'Aug',
    keyShort: 'Aug',
    keyLong: 'Aug',
    value: -3366,
  },
  {
    key: 'Sep',
    keyShort: 'Sep',
    keyLong: 'Sep',
    value: 3325,
  },
  {
    key: 'Oct',
    keyShort: 'Oct',
    keyLong: 'Oct',
    value: 103,
  },
  {
    key: 'Nov',
    keyShort: 'Nov',
    keyLong: 'Nov',
    value: 3755,
  },
  {
    key: 'Dec',
    keyShort: 'Dec',
    keyLong: 'Dec',
    value: 3609,
  },
];

const SeasonalityChart = ({
  metricsHistoricalData,
  metricKey,
  selectedPercentileInMainKPICard,
  currency,
}) => {
  const isAccessDenied = useCannotAccessMainKPI();

  const getMonthOrder = () => {
    const monthOrder: string[] = [];

    for (const monthMetricsObj of metricsHistoricalData) {
      monthOrder.push(monthMetricsObj['month']);
    }

    monthOrder.sort((a, b) => shortMonthNames.indexOf(a) - shortMonthNames.indexOf(b));

    return monthOrder;
  };

  const processMonthlyDataPercentileWise = (
    monthName,
    year,
    currentMonthObject,
    monthAvgMetricsMap
  ) => {
    if (!monthAvgMetricsMap[monthName]) {
      monthAvgMetricsMap[monthName] = {
        '25': [],
        '50': [],
        '75': [],
        '90': [],
      };
    }

    if (!currentMonthObject[year]) return;

    const targetYearData = currentMonthObject[year];

    for (const percentile of PERCENTILES) {
      const percentileKey = metricKey + percentile;

      if (targetYearData[percentileKey]) {
        monthAvgMetricsMap[monthName][percentile] = [
          ...monthAvgMetricsMap[monthName][percentile],
          targetYearData[percentileKey],
        ];
      }
    }
  };

  const processAverageOfMonthAvgMetricsMap = monthAvgMetricsMap => {
    for (const month in monthAvgMetricsMap) {
      for (const percentile of PERCENTILES) {
        if (!monthAvgMetricsMap[month][percentile]) continue;
        monthAvgMetricsMap[month][percentile] = d3.mean(monthAvgMetricsMap[month][percentile]);
      }
    }
  };

  const getHistoricalAverageMetrics = monthAvgMetricsMap => {
    const metricsArr: number[] = [];
    for (const month in monthAvgMetricsMap) {
      metricsArr.push(monthAvgMetricsMap[month][selectedPercentileInMainKPICard]);
    }

    return d3.mean(metricsArr);
  };

  const prepareGraphData = (monthAvgMetricsMap, averageMetrics: number) => {
    const months = getMonthOrder();
    const results: { [key: string]: number | string }[] = [];

    for (const month of months) {
      const currentMonthAvgMetrics = monthAvgMetricsMap[month][selectedPercentileInMainKPICard];
      // const differenceInPercentage = getPercentage(currentMonthAvgMetrics, averageMetrics);
      const differenceWithAvg = Math.floor(
        convertCurrency(currency, currentMonthAvgMetrics - averageMetrics)
      );

      results.push({
        key: month,
        keyShort: month,
        keyLong: month,
        value: differenceWithAvg,
      });
    }

    return results;
  };

  const processHistoricalMonthlyAvg = () => {
    const monthAvgMetricsMap = {};

    for (const currentMonthObject of metricsHistoricalData) {
      const monthName = currentMonthObject['month'];
      const latestYear = currentMonthObject['latestYear'];
      const previousYear = currentMonthObject['previousYear'];
      const twoYearsBefore = currentMonthObject['twoYearsBefore'];

      processMonthlyDataPercentileWise(
        monthName,
        latestYear,
        currentMonthObject,
        monthAvgMetricsMap
      );
      processMonthlyDataPercentileWise(
        monthName,
        previousYear,
        currentMonthObject,
        monthAvgMetricsMap
      );
      processMonthlyDataPercentileWise(
        monthName,
        twoYearsBefore,
        currentMonthObject,
        monthAvgMetricsMap
      );
    }

    processAverageOfMonthAvgMetricsMap(monthAvgMetricsMap);

    const averageMetrics = getHistoricalAverageMetrics(monthAvgMetricsMap);

    const graphData = prepareGraphData(monthAvgMetricsMap, averageMetrics);

    return graphData;
  };

  const graphData = isAccessDenied
    ? dummyGraphData
    : ((processHistoricalMonthlyAvg() as unknown) as BarGraphData[]);

  const Legends = () => {
    return (
      <Grid
        container
        item
        justifyContent={'center'}
        alignItems={'center'}
        xs={12}
        sx={{ marginTop: '20px' }}
      >
        <Grid
          item
          container
          xs={4}
          justifyContent={'flex-end'}
          alignItems={'center'}
          sx={{ gap: '10px', marginRight: '20px' }}
        >
          <Box
            sx={{
              width: '10px',
              height: '10px',
              background: COLOR_PRIMARY_1_800,
            }}
          />
          <Typography variant="body2">High Season</Typography>
        </Grid>
        <Grid
          item
          container
          xs={4}
          justifyContent={'flex-start'}
          alignItems={'center'}
          sx={{ gap: '10px' }}
        >
          <Box
            sx={{
              width: '10px',
              height: '10px',
              background: COLOR_SECONDARY_2_600,
            }}
          />
          <Typography variant="body2">Low Season</Typography>
        </Grid>
      </Grid>
    );
  };

  const getTooltipText = (data: number, key: string) => {
    if (!data || isNaN(data)) {
      return '';
    }
    const lessOrMore = Number(data) < 0 ? 'less' : 'more';
    data = Math.abs(data);
    const formattedCurrency = abbreviateNumber(convertCurrency(currency, data));
    return `Listings earn ${exchangeRates[currency].symbol}${formattedCurrency} ${lessOrMore} revenue than a typical month.`;
  };

  return (
    <Box
      sx={{
        width: '100%',
        // height: '350px',
        // boxShadow: '0 4px 10px #0003',
        borderRadius: '8px',
      }}
    >
      <Grid container xs={12} sx={{ padding: '10px' }}>
        {/* <Grid item xs={8}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Seasonality
          </Typography>
        </Grid> */}
        <Grid item xs={12}>
          <DynamicBarChart
            layout="horizontal"
            data={graphData}
            unit={currency}
            label="Sessonility"
            height={170}
            barColor={COLOR_PRIMARY_1_800}
            barAccentColor={COLOR_PRIMARY_1_500}
            useShortHand={true}
            isPropertySizeGraph={true}
            negitiveBarColor={COLOR_SECONDARY_2_600}
            negativeBarAccentColor={COLOR_SECONDARY_2_800}
            getTooltipText={getTooltipText}
            tickFormattorXAxis={getMlaMonthName}
          />
        </Grid>
        <Grid item xs={12}>
          <Legends />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SeasonalityChart;
